import { createSelector } from '@reduxjs/toolkit'

import type { RootState } from 'store/rootReducer'

import { selectGifts } from '../model/selectors/select-gifts'
import type { SetCartInfoProps, TCartItem } from '../types/cart-item'

import { buildSubmitGiftsPayload } from './gifts-helpers'

// В iport товары хранятся в RTK Query: берём последний полученный ответ корзины.
// Не импортируем cartApi, чтобы избежать циклической зависимости с apiSlice.
const selectBasketProducts = createSelector([(state: RootState) => state.api.queries], queries => {
	const basket = Object.values(queries)
		.filter(query => query?.data && ['getCartInfo', 'getCartItems'].includes(query.endpointName))
		.sort((a, b) => (b.fulfilledTimeStamp ?? 0) - (a.fulfilledTimeStamp ?? 0))[0]
	const data = basket?.data as SetCartInfoProps | TCartItem[] | undefined
	return Array.isArray(data) ? data : data?.PRODUCTS ?? []
})

// Используем существующую логику заказа: выбранный ONE или применённый ALL,
// с учётом исключений и только для товаров текущей корзины.
export const selectBasketGiftIds = createSelector(
	[selectBasketProducts, (state: RootState) => state.cart, selectGifts.selectAll],
	(products, cart, selectedGifts): number[] => {
		const allGiftsByProduct = { ...cart.allGiftsByProduct }
		const excludedAllFlagByProduct = { ...cart.excludedAutoGiftsByProduct }
		// Как и карточка подарка, сначала читаем восстановленную конфигурацию.
		// Старые поля используем только для совместимости, а не вместо выбранного пакета.
		const selectedOneGifts = products.flatMap(({ ID }) => {
			const config = cart.productGiftsConfig[ID]
			if (config?.type === 'packages') {
				allGiftsByProduct[ID] =
					config.selectedPackageIndex !== null ? config.availablePackages[config.selectedPackageIndex] ?? [] : []
				excludedAllFlagByProduct[ID] = config.autoGiftsExcluded
				return []
			}
			if (config?.type === 'one') allGiftsByProduct[ID] = []
			const gift = config?.selectedGift || selectedGifts.find(gift => gift.PRODUCT_IDS[0] === ID)
			return gift ? [{ ...gift, PRODUCT_IDS: [ID] }] : []
		})
		const { one, all } = buildSubmitGiftsPayload({
			cartProducts: products.map(product => ({ ID: product.ID, QUANTITY: product.QUANTITY ?? 1 })),
			selectedOneGifts,
			allGiftsByProduct,
			excludedAllFlagByProduct,
			excludedIdsByProduct: cart.excludedAutoGiftIdsByProduct,
			type: 'regular'
		})
		return Array.from(new Set([...one, ...all])).sort((a, b) => a - b)
	}
)

// Бэкенд ожидает повторяющиеся ключи GIFTS[], а не ID через запятую.
export const getBasketGiftParams = (giftIds: number[]) => {
	const params = new URLSearchParams()
	for (const id of giftIds) params.append('GIFTS[]', String(id))
	return params
}
